import { ChangeDetectionStrategy, Component, input, signal } from '@angular/core';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import type { SnapshotMetrics } from '../financial-snapshot.model';
import type { SpendingMixSubItem } from '../compute/financial-snapshot-compute';

@Component({
  selector: 'bn-financial-snapshot-monthly-summary',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CurrencyPipe, DecimalPipe],
  template: `
    <div class="bn-fs-summary">
      <div class="bn-fs-summary__top">
        <h3 class="bn-fs-summary__heading">Monthly summary</h3>
        <div class="bn-fs-summary__actions">
          <ng-content select="[fsActions]" />
        </div>
      </div>
      <dl class="bn-fs-summary__metrics">
        <div class="bn-fs-summary__row">
          <dt>
            <button
              type="button"
              class="bn-fs-summary__income-btn"
              [attr.aria-expanded]="incomeExpanded()"
              (click)="incomeExpanded.set(!incomeExpanded())"
            >
              <i
                class="pi bn-fs-summary__chev"
                [class.pi-chevron-up]="incomeExpanded()"
                [class.pi-chevron-right]="!incomeExpanded()"
                aria-hidden="true"
              ></i>
              Household income
            </button>
          </dt>
          <dd>
            @if (metrics().householdIncome === null) {
              —
            } @else {
              {{ metrics().householdIncome | currency: 'USD' : 'symbol' : '1.0-0' }}
            }
          </dd>
        </div>
        @if (incomeExpanded() && incomeSubItems().length) {
          <ul class="bn-fs-summary__subs">
            @for (sub of incomeSubItems(); track sub.key) {
              <li class="bn-fs-summary__sub">
                <span>{{ sub.label }}</span>
                <span>
                  @if (sub.amount === null) {
                    —
                  } @else {
                    {{ sub.amount | currency: 'USD' : 'symbol' : '1.0-0' }}
                  }
                </span>
              </li>
            }
          </ul>
        }
        <div class="bn-fs-summary__row">
          <dt>Monthly costs</dt>
          <dd>
            @if (metrics().monthlyCosts === null) {
              —
            } @else {
              {{ metrics().monthlyCosts | currency: 'USD' : 'symbol' : '1.0-0' }}
            }
          </dd>
        </div>
      </dl>
      <div
        class="bn-fs-summary__net"
        [class.bn-fs-summary__net--shortfall]="metrics().netCashflowStatus === 'shortfall'"
        [class.bn-fs-summary__net--surplus]="metrics().netCashflowStatus === 'surplus'"
      >
        <div class="bn-fs-summary__net-label">Net cashflow</div>
        <div class="bn-fs-summary__net-value">
          @if (metrics().netCashflow === null) {
            —
          } @else {
            {{ metrics().netCashflow | currency: 'USD' : 'symbol' : '1.0-0' }}
          }
        </div>
        @if (helperText(); as helper) {
          <p class="bn-fs-summary__net-helper">{{ helper }}</p>
        }
      </div>
      @if (metrics().costsVsIncomePercent !== null) {
        <div class="bn-fs-summary__bar-block">
          <div class="bn-fs-summary__bar-label">
            <span>COSTS VS INCOME</span>
            <span>{{ metrics().costsVsIncomePercent | number: '1.0-0' }}% of income</span>
          </div>
          <div class="bn-fs-summary__bar-track" role="presentation">
            <div class="bn-fs-summary__bar-fill" [style.width.%]="barWidth()"></div>
          </div>
        </div>
      }
    </div>
  `,
  styleUrl: './monthly-summary.component.scss',
})
export class FinancialSnapshotMonthlySummaryComponent {
  readonly metrics = input.required<SnapshotMetrics>();
  readonly incomeSubItems = input<SpendingMixSubItem[]>([]);
  readonly incomeExpanded = signal(false);

  barWidth(): number {
    const pct = this.metrics().costsVsIncomePercent;
    if (pct === null) {
      return 0;
    }
    return Math.min(100, Math.max(0, pct));
  }

  helperText(): string | null {
    const net = this.metrics().netCashflow;
    const status = this.metrics().netCashflowStatus;
    if (net === null || status === null) {
      return null;
    }
    const abs = Math.abs(Math.round(net));
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(abs);
    if (status === 'shortfall') {
      return `Spending exceeds income by about ${formatted}/month.`;
    }
    if (status === 'surplus') {
      return `Income exceeds spending by about ${formatted}/month.`;
    }
    return 'Income and spending are about even.';
  }
}
